import { type JSX, useRef } from 'react'
import { useIntl } from 'react-intl'

import { Dialog, DialogTitle } from 'base-ui'

import { useOpenableControls } from '../../generic-hooks/useOpenableControls/useOpenableControls'
import { StaticImage } from '../../image/StaticImage'

import { ErxModalContent } from './ErxModalContent'
import { useOpenModal } from './hooks/useErxModalOpenModal'
import { useErxModalShouldBeShown } from './hooks/useErxModalShouldBeShown'
import { useUserInteractionGate } from './hooks/useErxModalUserInteractionGate'
import modalHero from './images/erx-modal-hero.png'

/**
 * The modal is automatically opened after a delay specified by ERX_MODAL_TIMEOUT,
 * but only after the user interacts with the page (mousedown, keydown, scroll, touchstart).
 * When the modal is opened, a 'popin' event is pushed to the GTM data layer.
 */
const ErxModal = (): JSX.Element => {
  const hasOpenedRef = useRef(false)
  const modalControls = useOpenableControls(false)
  const { formatMessage } = useIntl()

  const { setErxModalShown, shouldBeShown } = useErxModalShouldBeShown()

  const openModalWithTimeout = useOpenModal(
    shouldBeShown,
    modalControls,
    setErxModalShown,
  )

  // Wait for user interaction before starting the modal timeout
  const handleFirstUserInteraction = (): void => {
    if (!hasOpenedRef.current) {
      openModalWithTimeout()
      hasOpenedRef.current = true
    }
  }

  useUserInteractionGate(handleFirstUserInteraction)

  return (
    <Dialog
      onClose={modalControls.handleClose}
      open={modalControls.isOpen}
      slotProps={{
        dialog: {
          className:
            'mobile-sm:w-11/12 max-w-xl bg-transparent h-auto mobile-sm:-mt-20',
        },
      }}
    >
      <div className="relative">
        <DialogTitle
          className="absolute right-3 top-3 bg-transparent p-0"
          onClose={modalControls.handleClose}
        />
      </div>
      <div className="mobile-sm:rounded-xl mobile-sm:rounded-b-none">
        <StaticImage
          alt={formatMessage({ id: 'erx.modal.recipe-hero.alt' })}
          className="m-auto w-full"
          src={modalHero}
          width={324}
        />
      </div>
      <ErxModalContent onModalClose={modalControls.handleClose} />
    </Dialog>
  )
}

export { ErxModal }
